'use client';

import { c as _c } from "react/compiler-runtime";
import * as SubframeCore from '@subframe/core';
import { FeatherCircleDashed } from '@subframe/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as SubframeUtils from '../utils';
import { Accordion } from './Accordion';
interface NavSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  isCollapsed?: boolean;
}
const NavSection = React.forwardRef<HTMLDivElement, NavSectionProps>(function NavSection(t0, ref) {
  const $ = _c(39);
  let children;
  let className;
  let label;
  let otherProps;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      label,
      icon: t1,
      className,
      isCollapsed: t2,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = label;
    $[4] = otherProps;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    label = $[3];
    otherProps = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const icon = t1 === undefined ? null : t1;
  const isCollapsed = t2 === undefined ? false : t2;
  if (isCollapsed) {
    let t3;
    if ($[7] !== className) {
      t3 = SubframeUtils.twClassNames("flex w-full flex-col items-center gap-1", className);
      $[7] = className;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== label) {
      t4 = label ? <span className="line-clamp-1 w-full px-0.5 text-center text-[7px] leading-tight font-bold tracking-wide break-all text-neutral-500 uppercase">{label}</span> : null;
      $[9] = label;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== children) {
      t5 = children ? <div className="flex w-full flex-col items-center gap-1">{children}</div> : null;
      $[11] = children;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== otherProps || $[14] !== ref || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
      t6 = <div className={t3} ref={ref} {...otherProps}>{t4}{t5}</div>;
      $[13] = otherProps;
      $[14] = ref;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  let t3;
  if ($[19] !== className) {
    t3 = SubframeUtils.twClassNames("group/36c53d81 flex w-full cursor-pointer flex-col items-start gap-3", className);
    $[19] = className;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== icon) {
    t4 = icon ? <SubframeCore.IconWrapper className="text-body font-body text-default-font">{icon}</SubframeCore.IconWrapper> : null;
    $[21] = icon;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== label) {
    t5 = label ? <span className="text-body-xs font-body-semibold text-subtext-color line-clamp-1 shrink-0 grow basis-0 tracking-wider">{label}</span> : null;
    $[23] = label;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Accordion.Chevron />;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t4 || $[27] !== t5) {
    t7 = <div className="flex w-full items-center gap-2 px-4 py-2 transition-colors">{t4}{t5}{t6}</div>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== children) {
    t8 = children ? <div className="flex w-full flex-col items-start gap-1">{children}</div> : null;
    $[29] = children;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t7 || $[32] !== t8) {
    t9 = <Accordion trigger={t7} defaultOpen={true}>{t8}</Accordion>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== otherProps || $[35] !== ref || $[36] !== t3 || $[37] !== t9) {
    t10 = <div className={t3} ref={ref} {...otherProps}>{t9}</div>;
    $[34] = otherProps;
    $[35] = ref;
    $[36] = t3;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
});
interface CollapsibleNavItemProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  icon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  selected?: boolean;
  isCollapsed?: boolean;
}
const CollapsibleNavItem = React.forwardRef<HTMLDivElement, CollapsibleNavItemProps>(function CollapsibleNavItem(t0, ref) {
  const $ = _c(93);
  let children;
  let className;
  let icon;
  let label;
  let otherProps;
  let rightSlot;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      children,
      label,
      icon,
      rightSlot,
      defaultOpen: t1,
      className,
      selected: t2,
      isCollapsed: t3,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = label;
    $[5] = otherProps;
    $[6] = rightSlot;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    label = $[4];
    otherProps = $[5];
    rightSlot = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  const defaultOpen = t1 === undefined ? false : t1;
  const selected = t2 === undefined ? false : t2;
  const isCollapsed = t3 === undefined ? false : t3;
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      top: 0,
      left: 0
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const [flyoutPosition, setFlyoutPosition] = useState(t4);
  const closeTimeoutRef = useRef(null);
  const isTriggerHoveredRef = useRef(false);
  const isContentHoveredRef = useRef(false);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const clearCloseTimeout = t5;
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      clearCloseTimeout();
    };
    t7 = [];
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      clearCloseTimeout();
      closeTimeoutRef.current = window.setTimeout(() => {
        if (!isTriggerHoveredRef.current && !isContentHoveredRef.current) {
          setIsFlyoutOpen(false);
        }
      }, 120);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const scheduleCloseIfNotHovered = t8;
  let t10;
  let t9;
  if ($[15] !== isCollapsed || $[16] !== isFlyoutOpen) {
    t9 = () => {
      if (!isCollapsed || !isFlyoutOpen) {
        return;
      }
      const handleClickOutside = event => {
        const target = event.target as Node;
        if (triggerRef.current?.contains(target)) {
          return;
        }
        if (contentRef.current?.contains(target)) {
          return;
        }
        setIsFlyoutOpen(false);
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t10 = [isCollapsed, isFlyoutOpen];
    $[15] = isCollapsed;
    $[16] = isFlyoutOpen;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[19] !== isCollapsed || $[20] !== isFlyoutOpen) {
    t11 = () => {
      if (!isCollapsed || !isFlyoutOpen) {
        return;
      }
      if (!triggerRef.current) {
        return;
      }
      const computePosition = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) {
          return;
        }
        const top = Math.max(8, Math.min(window.innerHeight - 8, rect.top + rect.height / 2));
        const left = Math.min(window.innerWidth - 288 - 8, rect.right + 8);
        setFlyoutPosition({
          top,
          left
        });
      };
      computePosition();
      window.addEventListener("resize", computePosition);
      window.addEventListener("scroll", computePosition, true);
      return () => {
        window.removeEventListener("resize", computePosition);
        window.removeEventListener("scroll", computePosition, true);
      };
    };
    t12 = [isCollapsed, isFlyoutOpen];
    $[19] = isCollapsed;
    $[20] = isFlyoutOpen;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  if (isCollapsed) {
    let t13;
    if ($[23] !== className || $[24] !== selected) {
      t13 = SubframeUtils.twClassNames("mx-auto flex aspect-square max-h-[56px] w-full max-w-[56px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md px-0.5 py-1 transition-all duration-200", {
        "bg-brand-primary/10 text-brand-primary": selected,
        "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900": !selected
      }, className);
      $[23] = className;
      $[24] = selected;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== ref) {
      t14 = node => {
        triggerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else {
          if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        }
      };
      $[26] = ref;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    let t16;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = () => {
        isTriggerHoveredRef.current = true;
        clearCloseTimeout();
        setIsFlyoutOpen(true);
      };
      t16 = () => {
        isTriggerHoveredRef.current = false;
        scheduleCloseIfNotHovered();
      };
      $[28] = t15;
      $[29] = t16;
    } else {
      t15 = $[28];
      t16 = $[29];
    }
    let t17;
    let t18;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = () => setIsFlyoutOpen(_temp);
      t18 = event_0 => {
        if (event_0.key === "Enter" || event_0.key === " ") {
          event_0.preventDefault();
          setIsFlyoutOpen(_temp2);
        }
        if (event_0.key === "Escape") {
          setIsFlyoutOpen(false);
        }
      };
      $[30] = t17;
      $[31] = t18;
    } else {
      t17 = $[30];
      t18 = $[31];
    }
    let t19;
    if ($[32] !== icon || $[33] !== selected) {
      t19 = icon ? <SubframeCore.IconWrapper className={SubframeUtils.twClassNames("text-body font-body flex-shrink-0 transition-colors", {
        "text-brand-primary": selected,
        "text-inherit": !selected
      })}>{icon}</SubframeCore.IconWrapper> : null;
      $[32] = icon;
      $[33] = selected;
      $[34] = t19;
    } else {
      t19 = $[34];
    }
    let t20;
    if ($[35] !== label || $[36] !== selected) {
      t20 = label ? <span className={SubframeUtils.twClassNames("line-clamp-1 w-full px-0.5 text-center text-[9px] leading-tight font-medium tracking-tight break-all transition-colors", {
        "text-brand-primary font-semibold": selected,
        "text-inherit": !selected
      })}>{label}</span> : null;
      $[35] = label;
      $[36] = selected;
      $[37] = t20;
    } else {
      t20 = $[37];
    }
    let t21;
    if ($[38] !== isFlyoutOpen || $[39] !== otherProps || $[40] !== t13 || $[41] !== t14 || $[42] !== t19 || $[43] !== t20) {
      t21 = <SubframeCore.Tooltip.Trigger asChild={true}><div className={t13} ref={t14} onMouseEnter={t15} onMouseLeave={t16} role="button" tabIndex={0} aria-haspopup="menu" aria-expanded={isFlyoutOpen} onClick={t17} onKeyDown={t18} {...otherProps}>{t19}{t20}</div></SubframeCore.Tooltip.Trigger>;
      $[38] = isFlyoutOpen;
      $[39] = otherProps;
      $[40] = t13;
      $[41] = t14;
      $[42] = t19;
      $[43] = t20;
      $[44] = t21;
    } else {
      t21 = $[44];
    }
    let t22;
    if ($[45] !== label) {
      t22 = <SubframeCore.Tooltip.Portal><SubframeCore.Tooltip.Content side="right" align="center" sideOffset={8}><div className="flex items-center gap-2 rounded-md border border-solid border-neutral-200 bg-white px-3 py-2 shadow-lg"><span className="text-body font-body text-default-font">{label}</span></div></SubframeCore.Tooltip.Content></SubframeCore.Tooltip.Portal>;
      $[45] = label;
      $[46] = t22;
    } else {
      t22 = $[46];
    }
    let t23;
    if ($[47] !== t21 || $[48] !== t22) {
      t23 = <SubframeCore.Tooltip.Provider><SubframeCore.Tooltip.Root>{t21}{t22}</SubframeCore.Tooltip.Root></SubframeCore.Tooltip.Provider>;
      $[47] = t21;
      $[48] = t22;
      $[49] = t23;
    } else {
      t23 = $[49];
    }
    let t24;
    if ($[50] !== children || $[51] !== flyoutPosition || $[52] !== isFlyoutOpen || $[53] !== label) {
      t24 = children && isFlyoutOpen ? createPortal(<div ref={contentRef} className="fixed z-40 w-72 -translate-y-1/2 rounded-md border border-solid border-neutral-200 bg-white p-2" style={{
        top: flyoutPosition.top,
        left: flyoutPosition.left
      }} role="menu" onMouseEnter={() => {
        isContentHoveredRef.current = true;
        clearCloseTimeout();
      }} onMouseLeave={() => {
        isContentHoveredRef.current = false;
        scheduleCloseIfNotHovered();
      }}><div className="px-2 py-1"><span className="text-body-xs font-body-semibold text-subtext-color tracking-wider">{label}</span></div><div className="mt-1 flex flex-col gap-1">{children}</div></div>, document.body) : null;
      $[50] = children;
      $[51] = flyoutPosition;
      $[52] = isFlyoutOpen;
      $[53] = label;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    let t25;
    if ($[55] !== t23 || $[56] !== t24) {
      t25 = <div className="relative w-full">{t23}{t24}</div>;
      $[55] = t23;
      $[56] = t24;
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    return t25;
  }
  let t13;
  if ($[58] !== className) {
    t13 = SubframeUtils.twClassNames("flex w-full flex-col", className);
    $[58] = className;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  let t14;
  if ($[60] !== selected) {
    t14 = SubframeUtils.twClassNames("group/collapsible-nav flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2 transition-all duration-200", {
      "bg-brand-primary/10": selected,
      "hover:bg-neutral-100 active:bg-neutral-200": !selected
    });
    $[60] = selected;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== icon || $[63] !== selected) {
    t15 = icon ? <SubframeCore.IconWrapper className={SubframeUtils.twClassNames("text-body font-body text-subtext-color transition-colors [&>svg]:h-[18px] [&>svg]:w-[18px]", {
      "text-brand-primary": selected
    })}>{icon}</SubframeCore.IconWrapper> : null;
    $[62] = icon;
    $[63] = selected;
    $[64] = t15;
  } else {
    t15 = $[64];
  }
  let t16;
  if ($[65] !== selected) {
    t16 = SubframeUtils.twClassNames("font-body text-subtext-color line-clamp-1 shrink-0 grow basis-0 text-[14px] transition-colors", {
      "text-brand-primary font-semibold": selected
    });
    $[65] = selected;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  let t17;
  if ($[67] !== label || $[68] !== t16) {
    t17 = <span className={t16}>{label}</span>;
    $[67] = label;
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  let t18;
  if ($[70] !== rightSlot) {
    t18 = rightSlot ? <div className="flex items-center">{rightSlot}</div> : null;
    $[70] = rightSlot;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  let t19;
  if ($[72] !== selected) {
    t19 = SubframeUtils.twClassNames("text-subtext-color transition-colors [&>svg]:h-[18px] [&>svg]:w-[18px]", {
      "text-brand-primary": selected
    });
    $[72] = selected;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== t19) {
    t20 = <Accordion.Chevron className={t19} />;
    $[74] = t19;
    $[75] = t20;
  } else {
    t20 = $[75];
  }
  let t21;
  if ($[76] !== t14 || $[77] !== t15 || $[78] !== t17 || $[79] !== t18 || $[80] !== t20) {
    t21 = <div className={t14}>{t15}{t17}{t18}{t20}</div>;
    $[76] = t14;
    $[77] = t15;
    $[78] = t17;
    $[79] = t18;
    $[80] = t20;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  let t22;
  if ($[82] !== children) {
    t22 = <div className="relative ml-4 flex w-[calc(100%-16px)] flex-col items-start gap-0.5 border-l border-dashed border-neutral-200 py-1 pl-2">{children}</div>;
    $[82] = children;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  let t23;
  if ($[84] !== defaultOpen || $[85] !== t21 || $[86] !== t22) {
    t23 = <Accordion trigger={t21} defaultOpen={defaultOpen}>{t22}</Accordion>;
    $[84] = defaultOpen;
    $[85] = t21;
    $[86] = t22;
    $[87] = t23;
  } else {
    t23 = $[87];
  }
  let t24;
  if ($[88] !== otherProps || $[89] !== ref || $[90] !== t13 || $[91] !== t23) {
    t24 = <div className={t13} ref={ref} {...otherProps}>{t23}</div>;
    $[88] = otherProps;
    $[89] = ref;
    $[90] = t13;
    $[91] = t23;
    $[92] = t24;
  } else {
    t24 = $[92];
  }
  return t24;
});
interface NavItemProps extends React.HTMLAttributes<HTMLDivElement> {
  selected?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  className?: string;
  isCollapsed?: boolean;
}
const NavItem = React.forwardRef<HTMLDivElement, NavItemProps>(function NavItem({
  selected = false,
  children,
  icon = <FeatherCircleDashed />,
  rightSlot,
  className,
  isCollapsed = false,
  ...otherProps
}: NavItemProps, ref) {
  if (isCollapsed) {
    return <SubframeCore.Tooltip.Provider>
        <SubframeCore.Tooltip.Root>
          <SubframeCore.Tooltip.Trigger asChild>
            <div className={SubframeUtils.twClassNames('group/fe62dd83 mx-auto flex aspect-square max-h-[56px] w-full max-w-[56px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md px-0.5 py-1 transition-all duration-200', {
            'bg-brand-primary/10 text-brand-primary': selected,
            'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900': !selected
          }, className)} ref={ref} {...otherProps}>
              {icon ? <SubframeCore.IconWrapper className={SubframeUtils.twClassNames('text-body font-body flex-shrink-0 transition-colors', {
              'text-brand-primary': selected,
              'text-inherit': !selected
            })}>
                  {icon}
                </SubframeCore.IconWrapper> : null}
              {children ? <span className={SubframeUtils.twClassNames('line-clamp-1 w-full px-0.5 text-center text-[9px] leading-tight font-medium tracking-tight break-all transition-colors', {
              'text-brand-primary font-semibold': selected,
              'text-inherit': !selected
            })}>
                  {children}
                </span> : null}
            </div>
          </SubframeCore.Tooltip.Trigger>
          <SubframeCore.Tooltip.Portal>
            <SubframeCore.Tooltip.Content side="right" align="center" sideOffset={8}>
              <div className="flex items-center gap-2 rounded-md border border-solid border-neutral-200 bg-white px-3 py-2 shadow-lg">
                <span className="text-body font-body text-default-font">{children}</span>
              </div>
            </SubframeCore.Tooltip.Content>
          </SubframeCore.Tooltip.Portal>
        </SubframeCore.Tooltip.Root>
      </SubframeCore.Tooltip.Provider>;
  }
  return <div className={SubframeUtils.twClassNames('group/fe62dd83 flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-1.5 transition-all duration-200', {
    'bg-brand-primary/10': selected,
    'hover:bg-neutral-100 active:bg-neutral-200': !selected
  }, className)} ref={ref} {...otherProps}>
      {icon ? <SubframeCore.IconWrapper className={SubframeUtils.twClassNames('text-body font-body text-subtext-color transition-colors [&>svg]:h-[18px] [&>svg]:w-[18px]', {
      'text-brand-primary': selected
    })}>
          {icon}
        </SubframeCore.IconWrapper> : null}
      {children ? <span className={SubframeUtils.twClassNames('font-body text-subtext-color line-clamp-1 shrink-0 grow basis-0 text-sm transition-colors', {
      'text-brand-primary font-semibold': selected
    })}>
          {children}
        </span> : null}
      {rightSlot ? <div className="flex items-center">{rightSlot}</div> : null}
    </div>;
});
interface SidebarWithCollapsibleSectionsRootProps extends React.HTMLAttributes<HTMLElement> {
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  isCollapsed?: boolean;
}
const SHIMMER_STYLE = `
@keyframes sidebar-shimmer {
  0%   { transform: translateX(-100%) skewX(-12deg); opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 0.6; }
  100% { transform: translateX(300%) skewX(-12deg); opacity: 0; }
}
.sidebar-shimmer-active {
  animation: sidebar-shimmer 650ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
}
`;
const SidebarWithCollapsibleSectionsRoot = React.forwardRef<HTMLElement, SidebarWithCollapsibleSectionsRootProps>(function SidebarWithCollapsibleSectionsRoot(t0, ref) {
  const $ = _c(47);
  let children;
  let className;
  let footer;
  let header;
  let otherProps;
  let t1;
  if ($[0] !== t0) {
    ({
      header,
      children,
      footer,
      className,
      isCollapsed: t1,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = footer;
    $[4] = header;
    $[5] = otherProps;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    footer = $[3];
    header = $[4];
    otherProps = $[5];
    t1 = $[6];
  }
  const isCollapsed = t1 === undefined ? false : t1;
  const shimmerRef = useRef(null);
  const prevCollapsedRef = useRef(isCollapsed);
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = shimmerRef.current;
      if (!el) {
        return;
      }
      el.classList.remove("sidebar-shimmer-active");
      el.classList.add("sidebar-shimmer-active");
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const triggerShimmer = t2;
  let t3;
  let t4;
  if ($[8] !== isCollapsed) {
    t3 = () => {
      if (prevCollapsedRef.current !== isCollapsed) {
        prevCollapsedRef.current = isCollapsed;
        triggerShimmer();
      }
    };
    t4 = [isCollapsed, triggerShimmer];
    $[8] = isCollapsed;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== className) {
    t5 = SubframeUtils.twClassNames("relative z-40 flex flex-col items-start overflow-hidden", className);
    $[11] = className;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = isCollapsed ? "72px" : "288px";
  const t7 = isCollapsed ? "8px" : "0px";
  const t8 = isCollapsed ? "16px" : "0px";
  const t9 = isCollapsed ? "calc(100vh - 16px)" : "100%";
  const t10 = isCollapsed ? "0px" : "1px";
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ["width 350ms cubic-bezier(0.4, 0, 0.2, 1)", "margin 350ms cubic-bezier(0.4, 0, 0.2, 1)", "border-radius 350ms cubic-bezier(0.4, 0, 0.2, 1)", "height 350ms cubic-bezier(0.4, 0, 0.2, 1)", "border-right-width 350ms cubic-bezier(0.4, 0, 0.2, 1)"];
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t6 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t12 = {
      width: t6,
      margin: t7,
      borderRadius: t8,
      height: t9,
      borderStyle: "solid",
      borderColor: "rgb(245,245,245)",
      borderWidth: "0px",
      borderRightWidth: t10,
      transition: t11.join(", ")
    };
    $[14] = t10;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = isCollapsed ? "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)" : "none";
  const t14 = isCollapsed ? "1px solid rgb(229, 231, 235)" : "none";
  const t15 = isCollapsed ? "16px" : "0px";
  let t16;
  if ($[20] !== t13 || $[21] !== t14 || $[22] !== t15) {
    t16 = <div data-sidebar-part="surface" aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      background: "white",
      opacity: 1,
      transition: "opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)",
      pointerEvents: "none",
      zIndex: 0,
      boxShadow: t13,
      border: t14,
      borderRadius: t15
    }} />;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <style>{SHIMMER_STYLE}</style>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div ref={shimmerRef} data-sidebar-part="shimmer" aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      width: "40%",
      background: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 50%, transparent 100%)",
      pointerEvents: "none",
      zIndex: 2,
      willChange: "transform"
    }} />;
    t19 = {
      position: "relative",
      zIndex: 1,
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%"
    };
    $[25] = t18;
    $[26] = t19;
  } else {
    t18 = $[25];
    t19 = $[26];
  }
  let t20;
  if ($[27] !== header || $[28] !== isCollapsed) {
    t20 = header ? <div data-sidebar-part="header" className="flex w-full flex-col items-start border-b border-solid" style={{
      gap: isCollapsed ? "4px" : "8px",
      padding: isCollapsed ? "4px" : "12px",
      borderColor: "rgb(250,250,250)",
      transition: ["gap 350ms cubic-bezier(0.4, 0, 0.2, 1)", "padding 350ms cubic-bezier(0.4, 0, 0.2, 1)", "border-color 400ms cubic-bezier(0.4, 0, 0.2, 1)"].join(", ")
    }}>{header}</div> : null;
    $[27] = header;
    $[28] = isCollapsed;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== children || $[31] !== isCollapsed) {
    t21 = children ? <div data-sidebar-part="content" className="scrollbar-thin flex w-full shrink-0 grow basis-0 flex-col items-start overflow-auto" style={{
      gap: isCollapsed ? "4px" : "16px",
      padding: isCollapsed ? "12px 4px" : "16px 12px",
      overflow: isCollapsed ? "hidden" : "auto",
      transition: ["gap 350ms cubic-bezier(0.4, 0, 0.2, 1)", "padding 350ms cubic-bezier(0.4, 0, 0.2, 1)"].join(", ")
    }}>{children}</div> : null;
    $[30] = children;
    $[31] = isCollapsed;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== footer || $[34] !== isCollapsed) {
    t22 = footer ? <div data-sidebar-part="footer" className="flex w-full flex-col items-start justify-end border-t border-solid" style={{
      gap: isCollapsed ? "4px" : "8px",
      padding: isCollapsed ? "12px 4px" : "16px 12px",
      borderColor: "rgb(250,250,250)",
      transition: ["gap 350ms cubic-bezier(0.4, 0, 0.2, 1)", "padding 350ms cubic-bezier(0.4, 0, 0.2, 1)", "border-color 400ms cubic-bezier(0.4, 0, 0.2, 1)"].join(", ")
    }}>{footer}</div> : null;
    $[33] = footer;
    $[34] = isCollapsed;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t20 || $[37] !== t21 || $[38] !== t22) {
    t23 = <div style={t19}>{t20}{t21}{t22}</div>;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== otherProps || $[41] !== ref || $[42] !== t12 || $[43] !== t16 || $[44] !== t23 || $[45] !== t5) {
    t24 = <nav className={t5} style={t12} ref={ref} {...otherProps}>{t16}{t17}{t18}{t23}</nav>;
    $[40] = otherProps;
    $[41] = ref;
    $[42] = t12;
    $[43] = t16;
    $[44] = t23;
    $[45] = t5;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
});
export const SidebarWithCollapsibleSections = Object.assign(SidebarWithCollapsibleSectionsRoot, {
  NavSection,
  NavItem,
  CollapsibleNavItem
});
function _temp(prev) {
  return !prev;
}
function _temp2(prev_0) {
  return !prev_0;
}